import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Video, ArrowUpRight } from "lucide-react";
import MarcarFeito from "@/components/MarcarFeito";
import ProcessoLink from "@/components/ProcessoLink";
import { EventTypeBadge } from "@/components/NewEventDialog";
import { SOURCE_LABELS, dateParts, jaPassou } from "@/components/CompromissoCard";
import { subtipoCompromisso } from "@/lib/compromisso";
import { formatCourtDistrict, formatParties, publicationDate, formatDate } from "@/lib/format";

// Lote 151 — "Próximos compromissos" do Painel: agrupado por DIA, horário grande à esquerda, tipo como título,
// partes e processo embaixo, detalhes que abrem na própria linha.
// A cor do tipo (a mesma da etiqueta EventTypeBadge) aparece na faixa à esquerda e na etiqueta pequena.
const FAIXA = {   // classes completas para o Tailwind enxergar
  audiencia: "border-l-gold-500", prazo: "border-l-stone-400",
  reuniao: "border-l-blue-300", pericia: "border-l-violet-400", lembrete: "border-l-teal-400", outros: "border-l-slate-400", compromisso: "border-l-blue-300",
};
const FASE = { inicial: "Audiência inicial", instrucao: "Audiência de instrução", julgamento: "Audiência de julgamento" };
const SEMANA = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

export function tipoChave(hearing) {
  const t = hearing?.event_type || "audiencia";
  if (t !== "reuniao") return t;
  return subtipoCompromisso(hearing)?.key || "compromisso";
}
export function tituloCompromisso(hearing) {
  const t = hearing?.event_type || "audiencia";
  if (t === "prazo") return hearing?.deadline_type || "Prazo";
  if (t === "reuniao") return subtipoCompromisso(hearing)?.label || "Compromisso";
  return FASE[hearing?.hearing_phase] || "Audiência";
}
function cabecalhoDia(iso, hoje) {
  const p = dateParts(iso);
  if (!p) return "Sem data";
  const [a, m, d] = iso.slice(0, 10).split("-").map(Number);
  const nome = SEMANA[new Date(a, m - 1, d).getDay()];
  return `${iso === hoje ? "Hoje · " : ""}${nome}, ${p.day} ${MESES[m - 1]}`;
}
function porDia(items) {
  const mapa = new Map();
  for (const h of items) {
    const k = h.hearing?.date || "";
    if (!mapa.has(k)) mapa.set(k, []);
    mapa.get(k).push(h);
  }
  return Array.from(mapa.entries()).sort((a, b) => (a[0] < b[0] ? -1 : 1));
}

function Linha({ h, hoje, userName, onToggleDone, aberta, onToggle }) {
  const hr = h.hearing || {};
  const type = hr.event_type || "audiencia";
  const passou = jaPassou(hr);
  const isToday = hr.date === hoje && !passou;
  const partes = formatParties(hr.parties) || hr.case_number || "—";
  const apoio = [hr.case_number ? null : null, formatCourtDistrict(hr.court, hr.court_district)].filter((x) => x && x !== "—");
  const baixavel = !!onToggleDone && (type === "prazo" || h.prazo_done === true || isToday);
  return (
    <div data-testid={`row-hearing-${h.pub_id}`} data-passou={passou ? "1" : "0"} data-tipo={tipoChave(hr)}
      className={`border-l-4 ${FAIXA[tipoChave(hr)] || FAIXA.audiencia} ${passou ? "bg-stone-50 opacity-80" : "bg-white"} border-t border-stone-100`}>
      <button type="button" data-testid={`toggle-hearing-${h.pub_id}`} aria-expanded={aberta} onClick={onToggle}
        className="w-full text-left flex items-stretch gap-3 md:gap-4 px-3 md:px-5 py-3 hover:bg-stone-50/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-500">
        <div className="w-[3.6rem] md:w-[4.6rem] shrink-0 border-r border-stone-200 pr-3 flex items-center">
          <span className={`font-display text-xl md:text-2xl font-bold tabular-nums leading-none ${isToday ? "text-gold-800" : "text-stone-900"}`}>{hr.time || "—"}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base md:text-lg font-semibold text-stone-900 leading-tight">{tituloCompromisso(hr)}</span>
            <EventTypeBadge hearing={hr} passou={passou} />
          </div>
          <div className={`text-sm truncate ${isToday ? "font-semibold text-stone-800" : "text-stone-700"}`} data-testid={isToday ? `today-highlight-${h.pub_id}` : undefined}>{partes}</div>
          <div className="text-xs text-stone-500 truncate">{[hr.case_number, ...apoio].filter(Boolean).join(" • ")}</div>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${aberta ? "rotate-180" : ""}`} strokeWidth={1.75} />
        </div>
      </button>
      {aberta && (
        <div className="px-3 md:px-5 pb-4" data-testid={`detalhes-${h.pub_id}`}>
          <div className="rounded-md bg-stone-50 border border-stone-100 p-4">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div><dt className="text-xs text-stone-500">Responsável</dt><dd className="font-medium text-stone-900">{userName || "—"}</dd></div>
              <div><dt className="text-xs text-stone-500">{type === "prazo" ? "Prazo" : "Modalidade"}</dt>
                <dd className="font-medium text-stone-900">{type === "prazo" ? (hr.deadline_type || "Prazo processual") : (hr.is_virtual ? "Videoconferência" : "Presencial")}</dd></div>
              <div><dt className="text-xs text-stone-500">Horário</dt><dd className="font-medium text-stone-900 tabular-nums">{hr.time || "—"}</dd></div>
            </dl>
            <div className="mt-3 pt-3 border-t border-stone-200 flex flex-wrap items-center justify-between gap-2 text-xs text-stone-500">
              <span>
                {h.source && <span data-testid={`source-label-${h.pub_id}`}>Origem: {SOURCE_LABELS[h.source] || h.source}</span>}
                {type !== "reuniao" && publicationDate(h) && <span className="ml-3" data-testid={`publicado-em-${h.pub_id}`}>Publicado em {formatDate(publicationDate(h))}</span>}
                {hr.case_number && <span className="ml-3">Processo <ProcessoLink numero={hr.case_number} /></span>}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link to={`/audiencia/${h.pub_id}`} data-testid={`open-hearing-${h.pub_id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 h-9 text-sm font-semibold text-stone-800 hover:bg-stone-100">
                Abrir detalhes <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
              {hr.is_virtual && hr.meeting_url && (
                <a href={hr.meeting_url} target="_blank" rel="noreferrer" data-testid={`acessar-${h.pub_id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 h-9 text-sm font-semibold text-stone-800 hover:bg-stone-100">
                  <Video className="w-4 h-4" strokeWidth={1.75} /> Acessar audiência
                </a>
              )}
              {baixavel && <div className="ml-auto"><MarcarFeito coluna h={h} onToggleDone={onToggleDone} /></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompromissosLista({ items, hoje, userName, onToggleDone }) {
  const [abertas, setAbertas] = useState(() => new Set());
  const alternar = (id) => setAbertas((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div data-testid="upcoming-hearings-list" className="md:mx-0 rounded-md overflow-hidden border border-stone-200 md:border-x-0 md:border-b-0 md:rounded-none">
      {porDia(items).map(([dia, lista]) => (
        <section key={dia || "sem-data"} data-testid={`dia-${dia}`}>
          <div className={`flex items-center justify-between px-3 md:px-5 py-2 text-[11px] font-bold tracking-widest uppercase ${dia === hoje ? "bg-gold-100 text-gold-800" : "bg-gold-100/50 text-gold-800"}`}>
            <span>{cabecalhoDia(dia, hoje)}</span>
            <span className="font-semibold normal-case tracking-normal">{lista.length} {lista.length === 1 ? "compromisso" : "compromissos"}</span>
          </div>
          {lista.map((h) => (
            <Linha key={h.pub_id} h={h} hoje={hoje} userName={userName} onToggleDone={onToggleDone}
              aberta={abertas.has(h.pub_id)} onToggle={() => alternar(h.pub_id)} />
          ))}
        </section>
      ))}
    </div>
  );
}
